import { APP_LOCALES } from '~/constants/appLocale'
import { blogCategoryTitle, blogLocaleFor, blogPostPath } from '~/constants/blog'
import { PRICES } from '~/constants/pricing'
import type { BlogPost, BlogPostListItem, CVData } from '~/types'

/**
 * Structured data as a single linked @graph.
 *
 * Previously each schema was emitted as its own <script> tag with entities
 * repeated inline — the homepage carried three unrelated Organization nodes
 * (standalone, WebSite.publisher, SoftwareApplication.publisher) and articles
 * added two more (Article.author, Article.publisher). Duplicated entities with no
 * shared identifier are a well-known source of entity-resolution ambiguity.
 *
 * Now: one script, one @graph, stable @ids, and every other reference is
 * `{ '@id': … }`. That also removes the duplicated logo definitions in one move.
 */

type Node = Record<string, unknown>

const SITE_NAME = 'InMyResume'
const CONTACT_EMAIL = 'info@inmyresume.com'

/**
 * Raster logo. Google's Organization and Article logo properties do not support
 * SVG, which is what this used to point at (/logo.svg).
 */
const LOGO_PATH = '/android-chrome-512x512.png'
const LOGO_SIZE = 512

/** Confirmed live, owned profiles. Anything unverified must not be listed here. */
const SAME_AS = [
  'https://www.linkedin.com/company/inmyresume',
  'https://x.com/inmyresume',
]

/**
 * Registered postal address.
 *
 * TODO: fill in streetAddress / addressLocality / postalCode. A PostalAddress
 * carrying only a country is close to useless and can suppress the whole
 * Organization panel, but inventing the missing parts would be worse. Confirmed
 * to exist — the values just need to be supplied.
 */
const ORG_ADDRESS: Node = {
  '@type': 'PostalAddress',
  addressCountry: 'CZ',
}

interface PersonSchema extends Node {
  '@type': 'Person'
  name: string
}

export const useJsonLd = () => {
  const { siteUrl, absUrl } = useSiteUrl()
  // Paths are passed as the English page ('/pricing'); a Czech page describes
  // its own URL (/cs/pricing) in its own language.
  const localize = useLocalizedPath()
  const nuxtApp = useNuxtApp()
  const at = (path: string) => `${siteUrl}${localize(path)}`
  const pageLanguage = () => nuxtApp.$i18n.locale.value

  const ORG_ID = `${siteUrl}/#organization`
  const SITE_ID = `${siteUrl}/#website`
  const LOGO_ID = `${siteUrl}/#logo`
  const APP_ID = `${siteUrl}/#software`

  // Absolute, not a bare '#organization': a bare fragment resolves against the
  // current page, so '#organization' on /blog would be a different entity than
  // '#organization' on /.
  const pageId = (path: string) => `${at(path)}#webpage`

  const organizationNode = (): Node => ({
    '@type': 'Organization',
    '@id': ORG_ID,
    name: SITE_NAME,
    url: `${siteUrl}/`,
    logo: {
      '@type': 'ImageObject',
      '@id': LOGO_ID,
      url: absUrl(LOGO_PATH),
      contentUrl: absUrl(LOGO_PATH),
      width: LOGO_SIZE,
      height: LOGO_SIZE,
      caption: SITE_NAME,
    },
    image: { '@id': LOGO_ID },
    description:
      'Professional CV and resume builder with AI-powered features. Create stunning resumes in minutes.',
    contactPoint: {
      '@type': 'ContactPoint',
      email: CONTACT_EMAIL,
      contactType: 'customer support',
      availableLanguage: ['en'],
    },
    sameAs: SAME_AS,
    address: ORG_ADDRESS,
    // NOTE: no `founder`/`foundingDate`. There used to be a founder typed as a
    // Person but named 'InMyResume Team', which is a schema-type error. A named
    // founder is worth adding once a real person is credited on an About page.
  })

  const webSiteNode = (): Node => ({
    '@type': 'WebSite',
    '@id': SITE_ID,
    url: `${siteUrl}/`,
    name: SITE_NAME,
    description:
      'Create professional CVs and resumes with our AI-powered builder. Paste your old CV or notes, choose a template, export to PDF.',
    publisher: { '@id': ORG_ID },
    // The languages the site has (constants/appLocale.ts), not a wish list.
    inLanguage: [...APP_LOCALES],
    potentialAction: {
      '@type': 'SearchAction',
      // EntryPoint form, per Google's sitelinks-searchbox documentation. The blog
      // listing reads ?search= during SSR, so this promise is actually kept.
      target: {
        '@type': 'EntryPoint',
        urlTemplate: `${siteUrl}/blog?search={search_term_string}`,
      },
      'query-input': 'required name=search_term_string',
    },
  })

  const softwareApplicationNode = (): Node => ({
    '@type': 'SoftwareApplication',
    '@id': APP_ID,
    name: 'InMyResume CV Builder',
    description:
      'AI-powered CV and resume builder with professional templates, PDF export and a shareable online CV.',
    url: `${siteUrl}/`,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web Browser',
    offers: {
      '@type': 'Offer',
      price: String(PRICES.monthly),
      priceCurrency: 'USD',
      // Computed, never a literal — a hardcoded date silently expires and starts
      // failing validation without anyone noticing.
      priceValidUntil: `${new Date().getUTCFullYear() + 1}-12-31`,
      availability: 'https://schema.org/InStock',
    },
    featureList: [
      'AI-powered CV enhancement',
      'Professional templates',
      'PDF export',
      'Online CV sharing',
      'ATS optimization',
      'Multi-language support',
      'AI cover letter generation',
      'AI job matching',
      'AI text enhancement',
      'AI CV analysis',
      'Web-based CV online',
    ],
    screenshot: absUrl('/og/home.png'),
    publisher: { '@id': ORG_ID },
  })

  const webPageNode = (options: {
    path: string
    name: string
    description?: string | null
    type?: 'WebPage' | 'CollectionPage' | 'ContactPage' | 'ProfilePage'
    hasBreadcrumb?: boolean
    primaryImage?: string | null
  }): Node => ({
    '@type': options.type ?? 'WebPage',
    '@id': pageId(options.path),
    url: at(options.path),
    name: options.name,
    ...(options.description ? { description: options.description } : {}),
    isPartOf: { '@id': SITE_ID },
    inLanguage: pageLanguage(),
    ...(options.hasBreadcrumb
      ? { breadcrumb: { '@id': `${at(options.path)}#breadcrumb` } }
      : {}),
    ...(options.primaryImage
      ? { primaryImageOfPage: { '@id': `${at(options.path)}#primaryimage` } }
      : {}),
  })

  const breadcrumbNode = (
    pagePath: string,
    items: Array<{ name: string; path?: string }>,
  ): Node => ({
    '@type': 'BreadcrumbList',
    '@id': `${at(pagePath)}#breadcrumb`,
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      // The last entry is the current page, so it carries no `item`.
      ...(item.path && index < items.length - 1 ? { item: at(item.path) } : {}),
    })),
  })

  const imageNode = (pagePath: string, url?: string | null): Node | null =>
    url
      ? {
          '@type': 'ImageObject',
          '@id': `${at(pagePath)}#primaryimage`,
          url: absUrl(url),
          contentUrl: absUrl(url),
        }
      : null

  const articleNode = (post?: BlogPost | BlogPostListItem | null): Node | null => {
    // Returns null instead of throwing: this runs inside a head getter, where a
    // throw is a fragile contract, and the graph filter drops nulls anyway.
    if (!post?.slug || !post.title) return null

    const path = blogPostPath(post)
    const words = 'content' in post && post.content ? countWords(post.content) : 0

    return {
      '@type': 'BlogPosting',
      '@id': `${siteUrl}${path}#article`,
      isPartOf: { '@id': pageId(path) },
      mainEntityOfPage: { '@id': pageId(path) },
      // Google truncates headlines past ~110 characters.
      headline: (title => (title.length > 110 ? `${title.slice(0, 107)}…` : title))(cleanTitle(post.title)),
      name: post.title,
      ...(post.meta_description || post.description
        ? { description: post.meta_description || post.description }
        : {}),
      // Omitted when the post has no image. A single generic placeholder repeated
      // across every article tells Google nothing and can suppress Discover images.
      ...(post.image_url ? { image: { '@id': `${siteUrl}${path}#primaryimage` } } : {}),
      author: { '@id': ORG_ID },
      publisher: { '@id': ORG_ID },
      datePublished: post.published_at || post.created_at,
      dateModified: post.updated_at || post.published_at || post.created_at,
      ...(post.category ? { articleSection: blogCategoryTitle(post.category) } : {}),
      ...(words > 0 ? { wordCount: words } : {}),
      ...(post.tags?.length ? { keywords: post.tags } : {}),
      inLanguage: blogLocaleFor(post.locale),
    }
  }

  const personNode = (cvData?: CVData | null): PersonSchema | null => {
    // No name, no node — this used to fall back to the literal string
    // 'Professional', which is a placeholder masquerading as data.
    if (!cvData?.name) return null

    const node: PersonSchema = {
      '@type': 'Person',
      name: cvData.name,
      ...(cvData.title ? { jobTitle: cvData.title } : {}),
      ...(cvData.aboutMe ? { description: cvData.aboutMe } : {}),
      ...(cvData.photoUrl ? { image: absUrl(cvData.photoUrl) } : {}),
      ...(cvData.contact?.website ? { url: cvData.contact.website } : {}),
      // NOTE: email and telephone are deliberately omitted. Publishing them in
      // machine-readable markup on a public page hands scrapers the PII directly.
    }

    if (cvData.experiences?.length) {
      node.worksFor = cvData.experiences.map(exp => ({
        '@type': 'Organization',
        name: exp.company,
      }))
    }

    if (cvData.education?.length) {
      node.alumniOf = cvData.education.map(edu => ({
        '@type': 'EducationalOrganization',
        name: edu.school,
      }))
    }

    if (cvData.technicalSkills?.length) {
      node.knowsAbout = cvData.technicalSkills
    }

    if (cvData.websites?.length) {
      node.sameAs = cvData.websites.map(site => site.url)
    }

    return node
  }

  const contactPageNode = (): Node => ({
    '@type': 'ContactPage',
    '@id': pageId('/contact'),
    url: at('/contact'),
    name: 'Contact InMyResume',
    description:
      'Get in touch with InMyResume team for support, questions, or feedback about our CV builder.',
    isPartOf: { '@id': SITE_ID },
    inLanguage: pageLanguage(),
    mainEntity: { '@id': ORG_ID },
  })

  /**
   * FAQPage builder. Used by /templates through MarketingFeaturePage's
   * `extraNodes`.
   *
   * Not for rich results: since Google's August 2023 change those are shown only
   * for authoritative government and health sites. It is emitted because the
   * question/answer pairs are then unambiguous to anything reading the graph,
   * which is what AI answer engines quote from. Do not add it to a page whose
   * questions are decorative — a FAQPage with two filler entries is noise.
   */
  const faqPageNode = (pagePath: string, faqs: Array<{ question: string; answer: string }>): Node | null =>
    faqs.length
      ? {
          '@type': 'FAQPage',
          '@id': `${at(pagePath)}#faq`,
          mainEntity: faqs.map(faq => ({
            '@type': 'Question',
            name: faq.question,
            acceptedAnswer: { '@type': 'Answer', text: faq.answer },
          })),
        }
      : null

  /**
   * HowTo builder, for a page that really is one procedure from start to
   * finish. Used by /how-to-write-a-resume.
   *
   * Like faqPageNode, this is not here for rich results — Google removed HowTo
   * results from desktop and mobile in 2023. It is here because the steps are
   * then unambiguous to anything reading the graph. Do not attach it to a page
   * that is a comparison or a list dressed up as steps.
   */
  const howToNode = (
    pagePath: string,
    options: { name: string; description?: string; steps: Array<{ name: string; text: string }>; totalTime?: string },
  ): Node | null =>
    options.steps.length
      ? {
          '@type': 'HowTo',
          '@id': `${at(pagePath)}#howto`,
          name: options.name,
          ...(options.description ? { description: options.description } : {}),
          // ISO 8601 duration, e.g. PT1H.
          ...(options.totalTime ? { totalTime: options.totalTime } : {}),
          inLanguage: pageLanguage(),
          step: options.steps.map((step, index) => ({
            '@type': 'HowToStep',
            position: index + 1,
            name: step.name,
            text: step.text,
            url: `${at(pagePath)}#step-${index + 1}`,
          })),
        }
      : null

  /**
   * Emits the whole graph as one idempotent <script type="application/ld+json">.
   *
   * Three details that matter:
   *
   * - `key` gives unhead a real dedupe key for the tag, so calling this again (or
   *   re-rendering) replaces the tag instead of appending another. The old
   *   emitters had no key and were called from watchEffect, which leaked a new
   *   never-removed script on every re-run.
   * - `innerHTML` is a getter, so it re-evaluates on dependency change without a
   *   watchEffect and stays bound to the component lifecycle.
   * - the value is an object, not a JSON string: unhead stringifies it and escapes
   *   '<' to < for json script types, so a post title containing </script>
   *   cannot break out of the tag.
   */
  const useJsonLdGraph = (nodes: MaybeRefOrGetter<Array<Node | null>>) => {
    useHead({
      script: [
        {
          key: 'jsonld-graph',
          id: 'jsonld-graph',
          type: 'application/ld+json',
          tagPriority: 'high',
          innerHTML: () => {
            const list = toValue(nodes).filter((node): node is Node => !!node)
            // `false` makes unhead drop the tag entirely rather than emitting an
            // empty <script></script> while data is still loading.
            return list.length ? { '@context': 'https://schema.org', '@graph': list } : false
          },
        },
      ],
    })
  }

  return {
    organizationNode,
    webSiteNode,
    softwareApplicationNode,
    webPageNode,
    breadcrumbNode,
    imageNode,
    articleNode,
    personNode,
    contactPageNode,
    faqPageNode,
    howToNode,
    useJsonLdGraph,
  }
}
